import React from 'react';
import { Box, CircularProgress, CssBaseline, Dialog, DialogContent } from '@mui/material';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { CONTEXTS } from 'figshelf-modules';
import { SocketProvider, useSocket } from './contexts/SocketContext';
import { UserStatusProvider } from './contexts/UserStatusContext';
import { ChatPopupProvider } from './contexts/ChatPopupContext';
import { AuthModalProvider } from './contexts/AuthModalContext';
import { LogoutBlockerProvider } from './contexts/LogoutBlockerContext';
import MessagesSocketService from './services/sub/MessagesSocketService';
import { ChatPopupManager } from './components/chat-popup';
import ChatPopupTestButton from './components/chat-popup/ChatPopupTestButton';
import AuthModal from './components/AuthModal';
import ProtectedRoute from './components/ProtectedRoute';
import IncomingCallHandler from './components/call/IncomingCallHandler';
import { CallOverlayProvider } from './contexts/CallOverlayContext';
import FloatingCallOverlay from './components/call/FloatingCallOverlay';
import MainLayout from './components/MainLayout';
import SettingsLayout from './components/SettingsLayout';
import usePremiumPaletteSync from './hooks/usePremiumPaletteSync';
import usePremiumAppIcon from './hooks/usePremiumAppIcon';
import SettingsPage from './components/SettingsPage';

// Import pages
const Home = React.lazy(() => import('./pages/Home'));

const ChatPopupLayer = () => {
  return (
    <>
      <ChatPopupManager />
      <ChatPopupTestButton />
    </>
  );
};

const EmailVerificationGate = () => {
  const auth = React.useContext(CONTEXTS.AuthContext);
  const location = useLocation();
  const [open, setOpen] = React.useState(false);

  const isEmailVerified =
    auth?.userModel?.isEmailVerified ??
    auth?.user?.isEmailVerified ??
    auth?.userModel?.user?.isEmailVerified;

  const isAuthRoute =
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/register') ||
    location.pathname.startsWith('/forgot-password') ||
    location.pathname.startsWith('/verify-2fa') ||
    location.pathname.startsWith('/verify-email');

  const shouldGate = Boolean(
    auth?.isSignedIn &&
      !auth?.isLoading &&
      isEmailVerified === false &&
      !isAuthRoute,
  );

  React.useEffect(() => {
    setOpen(shouldGate);
  }, [shouldGate]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <React.Suspense fallback={<AppRouteFallback />}>
          <VerifyEmail embedded={true} onComplete={() => setOpen(false)} />
        </React.Suspense>
      </DialogContent>
    </Dialog>
  );
};
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const Verify2FA = React.lazy(() => import('./pages/Verify2FA'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ProfileSetup = React.lazy(() => import('./pages/ProfileSetup'));
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'));
const Messages = React.lazy(() => import('./pages/Messages'));
const Database = React.lazy(() => import('./pages/Database'));
const DatabaseAll = React.lazy(() => import('./pages/DatabaseAll'));
const LatestReleasesAll = React.lazy(() => import('./pages/LatestReleasesAll'));
const SubmitToDatabase = React.lazy(() => import('./pages/SubmitToDatabase'));
const Marketplace = React.lazy(() => import('./pages/Marketplace'));
const Profile = React.lazy(() => import('./pages/Profile'));
const UpdateProfile = React.lazy(() => import('./pages/UpdateProfile'));
const CreateFigure = React.lazy(() => import('./pages/CreateFigure'));
const CreateCompany = React.lazy(() => import('./pages/CreateCompany'));
const CreateGroup = React.lazy(() => import('./pages/CreateGroup'));
const CompanyPreview = React.lazy(() => import('./pages/CompanyPreview'));
const PendingCompanies = React.lazy(() => import('./pages/PendingCompanies'));
const PendingFigures = React.lazy(() => import('./pages/PendingFigures'));
const GroupPreview = React.lazy(() => import('./pages/GroupPreview'));
const GroupManagement = React.lazy(() => import('./pages/GroupManagement'));
const CompanyManagement = React.lazy(() => import('./pages/CompanyManagement'));
const CompanyFigureDatabase = React.lazy(() => import('./pages/CompanyFigureDatabase'));
const SearchResults = React.lazy(() => import('./pages/SearchResults'));
const CallScreen = React.lazy(() => import('./pages/CallScreen'));
const Settings = React.lazy(() => import('./pages/Settings'));
const SignInAndSecurity = React.lazy(() => import('./pages/SignInAndSecurity'));
const ManageEmails = React.lazy(() => import('./pages/ManageEmails'));
const ManagePhones = React.lazy(() => import('./pages/ManagePhones'));
const ChangePassword = React.lazy(() => import('./pages/ChangePassword'));
const TwoStepVerification = React.lazy(() => import('./pages/TwoStepVerification'));
const AccountManagement = React.lazy(() => import('./pages/AccountManagement'));
const DeleteAccount = React.lazy(() => import('./pages/DeleteAccount'));
const Activity = React.lazy(() => import('./pages/Activity'));
const HelpSupport = React.lazy(() => import('./pages/HelpSupport'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const ProfileDetails = React.lazy(() => import('./pages/ProfileDetails'));
const ComingSoon = React.lazy(() => import('./pages/ComingSoon'));
const Report = React.lazy(() => import('./pages/Report'));
const GroupsList = React.lazy(() => import('./pages/GroupsList'));
const CompaniesBrowse = React.lazy(() => import('./pages/CompaniesBrowse'));
const GroupsBrowse = React.lazy(() => import('./pages/GroupsBrowse'));
const RankingBrowse = React.lazy(() => import('./pages/RankingBrowse'));
const FollowersFollowing = React.lazy(() => import('./pages/FollowersFollowing'));
const BlockList = React.lazy(() => import('./pages/BlockList'));
const PostPreview = React.lazy(() => import('./pages/PostPreview'));
const FigureDetailPage = React.lazy(() => import('./pages/FigureDetailPage'));
const DatabaseEntryDetailPage = React.lazy(() => import('./pages/DatabaseEntryDetailPage'));
const ArchivedPosts = React.lazy(() => import('./pages/ArchivedPosts'));
const HashtagPage = React.lazy(() => import('./pages/HashtagPage'));
const ProfileViewsStatistics = React.lazy(() => import('./pages/ProfileViewsStatistics'));
const EncyclopediaPage = React.lazy(() => import('./pages/EncyclopediaPage'));
const EncyclopediaEntryDetailPage = React.lazy(() => import('./pages/EncyclopediaEntryDetailPage'));
const OwnedByBrand = React.lazy(() => import('./pages/OwnedByBrand'));
const AdminUsers = React.lazy(() => import('./pages/AdminUsers'));
const AdminReports = React.lazy(() => import('./pages/AdminReports'));
const PremiumSettings = React.lazy(() => import('./pages/PremiumSettings'));
const Supporter = React.lazy(() => import('./pages/Supporter'));

const ShelfWiki = React.lazy(() => import('./pages/ShelfWiki'));
const WikiCreator = React.lazy(() => import('./pages/WikiCreator'));
const WikiScreen = React.lazy(() => import('./pages/WikiScreen'));
const WikiArticleDetail = React.lazy(() => import('./pages/WikiArticleDetail'));
const GuestLanding = React.lazy(() => import('./pages/GuestLanding'));

// Import settings pages
const NotificationsSettings = React.lazy(() => import('./pages/settings/NotificationsSettings'));
const AppearanceSettings = React.lazy(() => import('./pages/settings/AppearanceSettings'));
const ThemeSettings = React.lazy(() => import('./pages/settings/ThemeSettings'));
const ThemeCustomizer = React.lazy(() => import('./pages/settings/ThemeCustomizer'));
const SubscriptionSettings = React.lazy(() => import('./pages/settings/SubscriptionSettings'));
const BackupSettings = React.lazy(() => import('./pages/settings/BackupSettings'));
const CompanySettings = React.lazy(() => import('./pages/settings/CompanySettings'));
const DiscountSettings = React.lazy(() => import('./pages/settings/DiscountSettings'));
const Support = React.lazy(() => import('./pages/settings/Support'));
const ContentPreferences = React.lazy(() => import('./pages/settings/ContentPreferences'));

// Message Management Pages
const NewMessage = React.lazy(() => import('./pages/NewMessage'));
const ArchivedMessages = React.lazy(() => import('./pages/ArchivedMessages'));
const MessageSettings = React.lazy(() => import('./pages/MessageSettings'));
const Nicknames = React.lazy(() => import('./pages/Nicknames'));
const MediaGrid = React.lazy(() => import('./pages/MediaGrid'));
const NewGroupChat = React.lazy(() => import('./pages/NewGroupChat'));

// Post Feature Pages
const PostSettings = React.lazy(() => import('./pages/PostSettings'));
const PostSchedule = React.lazy(() => import('./pages/PostSchedule'));
const SavedPosts = React.lazy(() => import('./pages/SavedPosts'));
const AvatarBannerPreview = React.lazy(() => import('./pages/AvatarBannerPreview'));

// Theme Customization Pages
const ColorTheme = React.lazy(() => import('./pages/ColorTheme'));
const LightMode = React.lazy(() => import('./pages/LightMode'));
const PostingCommenting = React.lazy(() => import('./pages/PostingCommenting'));
const CommentsReaction = React.lazy(() => import('./pages/CommentsReaction'));

// Utility Pages
const BackupDownload = React.lazy(() => import('./pages/BackupDownload'));
const CropImage = React.lazy(() => import('./pages/CropImage'));
const SelectPeople = React.lazy(() => import('./pages/SelectPeople'));
const SelectFigure = React.lazy(() => import('./pages/SelectFigure'));
const SelectCompany = React.lazy(() => import('./pages/SelectCompany'));
const SelectUserFigure = React.lazy(() => import('./pages/SelectUserFigure'));

// Settings Workflow Pages
const PasswordConfirm = React.lazy(() => import('./pages/PasswordConfirm'));
const ConfirmDelete = React.lazy(() => import('./pages/ConfirmDelete'));
const GroupSettingsPage = React.lazy(() => import('./pages/GroupSettingsPage'));
const ServerDown = React.lazy(() => import('./pages/ServerDown'));

const PremiumPaletteSyncer = () => { usePremiumPaletteSync(); return null; };
const PremiumAppIconSyncer = () => { usePremiumAppIcon(); return null; };

const AppRouteFallback = () => (
  <Box
    sx={{
      width: '100%',
      minHeight: '40vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <CircularProgress size={28} />
  </Box>
);

// Wrapper component to provide MessagesProvider with socket service
const AppWithMessages = ({ children }) => {
  const { MessagesProvider, useAuth } = CONTEXTS;
  const { messages: messagesSocketService } = useSocket();
  const { user } = useAuth();

  return (
    <MessagesProvider user={user} messagesSocketService={messagesSocketService}>
      {children}
    </MessagesProvider>
  );
};

// Wrapper component to provide NotificationProvider with socket service
const AppWithNotifications = ({ children }) => {
  const { NotificationProvider } = CONTEXTS;
  const { notifications: notificationSocketService } = useSocket();

  const UniversalPushNotificationsBridge = () => {
    const { universal } = useSocket();
    const { showPushNotification } = CONTEXTS.useNotifications();
    const recentToastKeysRef = React.useRef(new Map());

    React.useEffect(() => {
      const sock = universal?.socket;
      if (!sock) return;

      try {
        console.log('[UniversalPushNotificationsBridge] attaching listeners', {
          hasUniversal: !!universal,
          hasSocket: !!universal?.socket,
          hasSocketIo: !!universal?.socket?.socket,
          isConnected: universal?.socket?.isConnected,
          at: new Date().toISOString(),
        });
      } catch (_) {}

      const cleanupFns = [];

      const shouldShowToast = (key) => {
        try {
          const now = Date.now();
          const ttlMs = 3000;
          const last = recentToastKeysRef.current.get(key);
          if (last && now - last < ttlMs) return false;
          recentToastKeysRef.current.set(key, now);

          // opportunistic cleanup
          for (const [k, t] of recentToastKeysRef.current.entries()) {
            if (now - t > ttlMs * 2) recentToastKeysRef.current.delete(k);
          }
          return true;
        } catch (_) {
          return true;
        }
      };

      const on = (event, handler) => {
        try {
          if (typeof sock.on === 'function') {
            sock.on(event, handler);
            cleanupFns.push(() => {
              try {
                if (typeof sock.off === 'function') sock.off(event, handler);
                else if (typeof sock.removeListener === 'function') sock.removeListener(event, handler);
              } catch (_) {}
            });
            return;
          }

          // Fallback: some socket wrappers expose the raw socket.io client as `sock.socket`
          const raw = sock?.socket;
          if (raw && typeof raw.on === 'function') {
            raw.on(event, handler);
            cleanupFns.push(() => {
              try {
                if (typeof raw.off === 'function') raw.off(event, handler);
                else if (typeof raw.removeListener === 'function') raw.removeListener(event, handler);
              } catch (_) {}
            });
          }
        } catch (_) {}
      };

      on('post:reacted', (data) => {
        try {
          console.log('[UniversalPushNotificationsBridge] event post:reacted', data);
        } catch (_) {}
        const reactorName = data?.reactor?.username || 'Someone';
        const icon = data?.reactionType || '👍';
        const iconUrl = data?.reactor?.avatar || null;
        showPushNotification('Post reaction', `${reactorName} reacted to your post`, 'info', { icon, iconUrl });
      });

      on('post:saved', (data) => {
        const saverName = data?.saver?.username || 'Someone';
        showPushNotification('Post saved', `${saverName} saved your post`, 'info', { icon: '🔖' });
      });

      on('post:shared', (data) => {
        const sharerName = data?.sharer?.username || 'Someone';
        showPushNotification('Post shared', `${sharerName} shared your post`, 'info', { icon: '🔁' });
      });

      on('post:new_comment', (data) => {
        const authorName = data?.commentAuthor?.username || 'Someone';
        const iconUrl = data?.commentAuthor?.avatar || null;
        showPushNotification('New comment', `${authorName} commented on your post`, 'info', { icon: '💬', iconUrl });
      });

      on('comment:reacted', (data) => {
        const key = `comment:reacted:${data?.commentId || ''}:${data?.reactor?.id || ''}:${data?.reactionType || ''}`;
        if (!shouldShowToast(key)) return;
        const reactorName = data?.reactor?.username || 'Someone';
        const icon = data?.reactionType || '👍';
        const iconUrl = data?.reactor?.avatar || null;
        showPushNotification('New reaction', `${reactorName} reacted to your comment`, 'info', { icon, iconUrl });
      });

      on('comment:replied', (data) => {
        const key = `comment:replied:${data?.parentCommentId || ''}:${data?.replyCommentId || data?.commentId || ''}`;
        if (!shouldShowToast(key)) return;
        const authorName = data?.replyAuthor?.username || 'Someone';
        const iconUrl = data?.replyAuthor?.avatar || null;
        showPushNotification('New reply', `${authorName} replied to your comment`, 'info', { icon: '💬', iconUrl });
      });

      on('user:followed_you', (data) => {
        const followerName = data?.follower?.username || 'Someone';
        const iconUrl = data?.follower?.avatar || null;
        showPushNotification('New follower', `${followerName} followed you`, 'info', { icon: '👤', iconUrl });
      });

      on('user:unfollowed_you', (data) => {
        const followerName = data?.follower?.username || 'Someone';
        const iconUrl = data?.follower?.avatar || null;
        showPushNotification('Unfollowed', `${followerName} unfollowed you`, 'info', { icon: '👤', iconUrl });
      });

      on('user:mentioned_you', (data) => {
        const mentionerName = data?.mentionedBy?.username || 'Someone';
        const iconUrl = data?.mentionedBy?.avatar || null;
        showPushNotification('Mentioned you', `${mentionerName} mentioned you`, 'info', { icon: '@', iconUrl });
      });

      on('feed:user:post_created', (data) => {
        const authorName = data?.author?.username || 'Someone';
        const iconUrl = data?.author?.avatar || null;
        showPushNotification('New post', `${authorName} created a new post`, 'info', { icon: '📝', iconUrl });
      });

      on('feed:user:figure_released', (data) => {
        const authorName = data?.user?.username || 'Someone';
        showPushNotification('Figure released', `${authorName} released a figure`, 'info', { icon: '📦' });
      });

      on('feed:user:figure_inserted', (data) => {
        const authorName = data?.user?.username || 'Someone';
        showPushNotification('Figure added', `${authorName} added a figure`, 'info', { icon: '➕' });
      });

      on('feed:company:figure_released', (data) => {
        const companyName = data?.company?.name || 'A company';
        showPushNotification('Company release', `${companyName} released a figure`, 'info', { icon: '🏭' });
      });

      on('wishlist:price_drop', (data) => {
        const figureName = data?.figure?.name || 'An item';
        showPushNotification('Price drop', `${figureName} dropped in price`, 'info', { icon: '🏷️' });
      });

      on('wishlist:back_in_stock', (data) => {
        const figureName = data?.figure?.name || 'An item';
        showPushNotification('Back in stock', `${figureName} is back in stock`, 'info', { icon: '✅' });
      });

      on('wishlist:restocked', (data) => {
        const figureName = data?.figure?.name || 'An item';
        showPushNotification('Restocked', `${figureName} has been restocked`, 'info', { icon: '📦' });
      });

      on('wishlist:preorder_open', (data) => {
        const figureName = data?.figure?.name || 'An item';
        showPushNotification('Preorder open', `${figureName} preorder is now open`, 'info', { icon: '🛒' });
      });

      on('wishlist:released', (data) => {
        const figureName = data?.figure?.name || 'An item';
        showPushNotification('Released', `${figureName} has been released`, 'info', { icon: '🎉' });
      });

      on('wishlist:discounted', (data) => {
        const figureName = data?.figure?.name || 'An item';
        showPushNotification('Discount', `${figureName} is discounted`, 'info', { icon: '💸' });
      });

      return () => {
        cleanupFns.forEach(fn => {
          try {
            fn();
          } catch (_) {}
        });
      };
    }, [universal?.socket, showPushNotification]);

    return null;
  };

  return (
    <NotificationProvider notificationSocketService={notificationSocketService}>
      <UniversalPushNotificationsBridge />
      {children}
    </NotificationProvider>
  );
};

const ProfileSetupGate = () => {
  const auth = React.useContext(CONTEXTS.AuthContext);
  const location = useLocation();
  const [open, setOpen] = React.useState(false);

  const onboarded =
    auth?.userModel?.settings?.onboarded ??
    auth?.settings?.onboarded ??
    auth?.user?.settings?.onboarded;

  const isAuthRoute =
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/register') ||
    location.pathname.startsWith('/forgot-password') ||
    location.pathname.startsWith('/verify-2fa');

  const shouldGate = Boolean(auth?.isSignedIn && !auth?.isLoading && onboarded === false && !isAuthRoute);

  React.useEffect(() => {
    setOpen(shouldGate);
  }, [shouldGate]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <React.Suspense fallback={<AppRouteFallback />}>
          <ProfileSetup hideSkip={true} embedded={true} onComplete={() => setOpen(false)} />
        </React.Suspense>
      </DialogContent>
    </Dialog>
  );
};

function App() {
  // Destructure the providers from CONTEXTS
  const { APIProvider, AuthProvider, ThemeProvider, NotificationProvider } = CONTEXTS;

  return (
    <GoogleOAuthProvider clientId="153910581280-gn1a1trjg94odg5ku49ic2crortk2tcc.apps.googleusercontent.com">
      <APIProvider>
        <AuthProvider>
          <ThemeProvider>
            <SocketProvider>
              <UserStatusProvider>
                <AppWithNotifications>
                  <AppWithMessages>
                    <ChatPopupProvider>
                      <AuthModalProvider>
                        <LogoutBlockerProvider>
                          <CssBaseline />
                          <PremiumPaletteSyncer />
                          <PremiumAppIconSyncer />
                          <BrowserRouter>
                            <CallOverlayProvider>
                              <EmailVerificationGate />
                              <ProfileSetupGate />
                              <IncomingCallHandler />
                              <FloatingCallOverlay />
                              <ChatPopupLayer />
                              <AuthModal />
                              <React.Suspense fallback={<AppRouteFallback />}>
                                <Routes>
                              <Route
                                path="/"
                                element={
                                  <MainLayout>
                                    <CONTEXTS.AuthContext.Consumer>
                                      {(auth) => (auth?.isSignedIn ? <Home /> : <GuestLanding />)}
                                    </CONTEXTS.AuthContext.Consumer>
                                  </MainLayout>
                                }
                              />

                              <Route
                                path="/verify-email"
                                element={
                                  <ProtectedRoute requireAuth={true}>
                                    <VerifyEmail />
                                  </ProtectedRoute>
                                }
                              />

                {/* Protected routes - require authentication */}
                
                <Route 
                  path="/messages" 
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <Messages />
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />

                <Route
                  path="/wiki"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <WikiScreen />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/shelf-wiki"
                  element={
                    <MainLayout>
                      <ShelfWiki />
                    </MainLayout>
                  }
                />

                <Route
                  path="/shelf-wiki/create"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <WikiCreator />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/shelf-wiki/edit/:id"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <WikiCreator />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/shelf-wiki/article/:id"
                  element={
                    <MainLayout>
                      <WikiArticleDetail />
                    </MainLayout>
                  }
                />
                
                <Route 
                  path="/database" 
                  element={
                    <MainLayout>
                      <Database />
                    </MainLayout>
                  } 
                />
                
                <Route 
                  path="/database/latest_releases" 
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <LatestReleasesAll />
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/database/all" 
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <DatabaseAll />
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/database/submit" 
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <SubmitToDatabase />
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/database/:entryId" 
                  element={
                    <MainLayout>
                      <DatabaseEntryDetailPage />
                    </MainLayout>
                  } 
                />

                <Route
                  path="/encyclopedia"
                  element={
                    <MainLayout>
                      <EncyclopediaPage />
                    </MainLayout>
                  }
                />

                <Route
                  path="/encyclopedia/:type/:value"
                  element={
                    <MainLayout>
                      <EncyclopediaEntryDetailPage />
                    </MainLayout>
                  }
                />
                
                <Route 
                  path="/figures/pending" 
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <PendingFigures />
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/figures/:figureId" 
                  element={
                    <MainLayout>
                      <DatabaseEntryDetailPage />
                    </MainLayout>
                  } 
                />
                
                <Route 
                  path="/marketplace" 
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <Marketplace />
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/search" 
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <SearchResults />
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />

                <Route
                  path="/companies"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <CompaniesBrowse />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/groups"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <GroupsBrowse />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/ranking"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <RankingBrowse />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/coming-soon"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <ComingSoon />
                    </ProtectedRoute>
                  }
                />
                
                <Route 
                  path="/post/:postId" 
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <PostPreview />
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <Profile />
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/user/:username" 
                  element={
                    <MainLayout>
                      <Profile />
                    </MainLayout>
                  } 
                />
                
                <Route 
                  path="/user/:userId/owned-by-brand" 
                  element={
                    <MainLayout>
                      <OwnedByBrand />
                    </MainLayout>
                  } 
                />
                
                <Route 
                  path="/user/id/:userId" 
                  element={
                    <MainLayout>
                      <Profile />
                    </MainLayout>
                  } 
                />
                
                <Route 
                  path="/profile-setup" 
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <ProfileSetup />
                    </ProtectedRoute>
                  } 
                />
                
                <Route
                  path="/profile/details"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <ProfileDetails />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/profile/edit"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <SettingsLayout>
                        <SettingsPage
                          title="Profile Details"
                          description="Manage your profile settings and preferences"
                          sx={{ px: 30 }}
                        >
                          <UpdateProfile />
                        </SettingsPage>
                      </SettingsLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/profile/views/statistics"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <SettingsLayout>
                        <ProfileViewsStatistics />
                      </SettingsLayout>
                    </ProtectedRoute>
                  }
                />

              <Route
                  path="/create-company"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <CreateCompany />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/companies/pending"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <PendingCompanies />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/create-figure"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <CreateFigure />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/create-group"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <CreateGroup />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/company/:companyId"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <CompanyPreview />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/company/:companyId/manage"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <CompanyManagement />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/company/:companyId/figures"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <CompanyFigureDatabase />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/group/:groupId"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <GroupPreview />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/group/:groupId/manage"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <GroupManagement />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/call"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <CallScreen />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <Settings />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/security"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <SignInAndSecurity />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/security/email"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <ManageEmails />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/security/phone"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <ManagePhones />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/security/password"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <ChangePassword />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/security/2fa"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <TwoStepVerification />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/account"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <AccountManagement />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/account/delete"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <DeleteAccount />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/account/deactivate"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <DeleteAccount />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/activity"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <SettingsLayout>
                        <Activity />
                      </SettingsLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/help-support"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <HelpSupport />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/privacy-policy"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <SettingsLayout>
                        <PrivacyPolicy />
                      </SettingsLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/report"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <Report />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/groups"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <GroupsList />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/followers"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <FollowersFollowing />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/blocking"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <BlockList />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/archive"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <SettingsLayout>
                        <ArchivedPosts />
                      </SettingsLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/hashtag/:tag"
                  element={
                    <MainLayout>
                      <HashtagPage />
                    </MainLayout>
                  }
                />

                <Route
                  path="/settings/notifications"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <NotificationsSettings />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/appearance"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <AppearanceSettings />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/theme"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <ThemeSettings />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/theme-customizer"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <ThemeCustomizer />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/subscription"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <SubscriptionSettings />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/backup"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <BackupSettings />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/company"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <CompanySettings />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/discount"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <DiscountSettings />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/support"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <Support />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings/content-preferences"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <ContentPreferences />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Message Management Routes */}
                <Route
                  path="/messages/new"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <NewMessage />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messages/archived"
                  element={ 
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <ArchivedMessages />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messages/settings"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <MessageSettings />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messages/nicknames"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <Nicknames />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messages/media"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <MediaGrid />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messages/new-group"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <NewGroupChat />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Post Feature Routes */}
                <Route
                  path="/post/:postId/settings"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <PostSettings />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/post/schedule"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <PostSchedule />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/saved"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <SettingsLayout>
                        <SavedPosts />
                      </SettingsLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/preview/avatar-banner"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <AvatarBannerPreview />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Theme Customization Routes */}
                <Route
                  path="/settings/theme/legacy"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <ColorTheme />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/appearance/legacy"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <LightMode />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/posting-commenting"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <PostingCommenting />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/comments-reaction"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <CommentsReaction />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Utility Routes */}
                <Route
                  path="/settings/backup/legacy"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <BackupDownload />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/crop-image"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <CropImage />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/select-people"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <SelectPeople />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/select-figure"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <SelectFigure />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/select-company"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <SelectCompany />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/select-user-figure"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <SelectUserFigure />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Settings Workflow Routes */}
                <Route
                  path="/password-confirm"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <PasswordConfirm />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/confirm-delete"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <ConfirmDelete />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/group/:groupId/settings"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <GroupSettingsPage />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Supporter / Payment Route */}
                <Route
                  path="/supporter"
                  element={<Supporter />}
                />

                {/* Server Status Route */}
                <Route
                  path="/server-down"
                  element={
                    <ServerDown />
                  }
                />

                {/* Public routes - redirect to home if already authenticated */}
                <Route 
                  path="/login" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <Login />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/register" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <Register />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/verify-2fa" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <Verify2FA />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/forgot-password" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <ForgotPassword />
                    </ProtectedRoute>
                  } 
                />
                
                <Route
                  path="/settings/premium"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <SettingsLayout>
                        <PremiumSettings />
                      </SettingsLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <AdminUsers />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/reports"
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MainLayout>
                        <AdminReports />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Catch all route - redirect to home */}
                <Route path="*" element={<Navigate to="/" replace />} />
                              </Routes>
                              </React.Suspense>
                            </CallOverlayProvider>
                          </BrowserRouter>
                        </LogoutBlockerProvider>
                        </AuthModalProvider>
                  </ChatPopupProvider>
                </AppWithMessages>
              </AppWithNotifications>
              </UserStatusProvider>
            </SocketProvider>
          </ThemeProvider>
        </AuthProvider>
      </APIProvider>
      </GoogleOAuthProvider>
  );
}

export default App;
